// redux/packageSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userRequest, publicRequest } from "../api/requestMethods";

// Calculate package price based on details and distance
export const calculatePackagePrice = createAsyncThunk(
    "package/calculatePrice",
    async ({ packageDetails, calculatedDistance }, { rejectWithValue }) => {
        try {
            const response = await publicRequest.post("/packages/calculate-price", {
                packageDetails,
                calculatedDistance,
            });
            return response.data.price;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to calculate price");
        }
    }
);

// Create a new package order
export const createPackageOrder = createAsyncThunk(
    "package/createOrder",
    async (packageData, { rejectWithValue }) => {
        try {
            const response = await userRequest.post("/packages", packageData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to create package order");
        }
    }
);

// Get all orders for a specific user
export const fetchUserPackageOrders = createAsyncThunk(
    "package/fetchUserOrders",
    async (userId, { rejectWithValue }) => {
        try {
            const response = await userRequest.get(`/packages/user-orders/${userId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to fetch package orders");
        }
    }
);

const initialState = {
    packageDetails: {
        weight: "",
        description: "",
        isFragile: false,
        specialInstructions: "",
        pickupAddress: null,
        deliveryAddress: null,
        customerNumber: "",
    },
    price: null,
    calculatedDistance: null,
    error: null,
    status: "idle",
    lastPriceCalculationParams: null,
    userOrders: [],
    userOrdersStatus: "idle",
    userOrdersError: null,
};

const packageSlice = createSlice({
    name: "package",
    initialState,
    reducers: {
        updatePackageDetails: (state, action) => {
            state.packageDetails = {
                ...state.packageDetails,
                ...action.payload,
            };
            if (action.payload.hasOwnProperty("isFragile")) {
                state.packageDetails.isFragile = action.payload.isFragile;
            }
            // Reset calculations when addresses change
            if (
                action.payload.pickupAddress === null ||
                action.payload.deliveryAddress === null
            ) {
                state.calculatedDistance = null;
                state.price = null;
            }
        },
        setCalculatedDistance: (state, action) => {
            state.calculatedDistance = action.payload.distanceInKm;
            // Reset price when distance changes
            state.price = null;
            state.status = "idle";
        },
        resetPackageState: () => initialState,
        setLastPriceCalculationParams: (state, action) => {
            state.lastPriceCalculationParams = action.payload;
        },
        // Update a specific order's status in real-time via socket
        updateOrderStatus: (state, action) => {
            const { orderId, status, trackingInfo } = action.payload;
            const orderIndex = state.userOrders.findIndex(
                (order) => order._id === orderId
            );
            if (orderIndex !== -1) {
                state.userOrders[orderIndex].delivery_status = status;
                if (trackingInfo) {
                    state.userOrders[orderIndex].currentLocation =
                        trackingInfo.currentLocation;
                    state.userOrders[orderIndex].estimatedDelivery =
                        trackingInfo.estimatedDelivery;
                }
            }
        },
        // Add a new order to the list (for real-time socket updates)
        addNewOrder: (state, action) => {
            const newOrder = action.payload;
            // Prepend to keep newest first
            state.userOrders = [newOrder, ...state.userOrders];
        },
        clearPackageError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Calculate Price
            .addCase(calculatePackagePrice.pending, (state) => {
                state.status = "loading";
            })
            .addCase(calculatePackagePrice.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.price = action.payload;
                state.error = null;
            })
            .addCase(calculatePackagePrice.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
                state.price = null;
            })
            // Create Order
            .addCase(createPackageOrder.pending, (state) => {
                state.status = "loading";
            })
            .addCase(createPackageOrder.fulfilled, (state) => {
                state.status = "succeeded";
                // Reset form state after successful order creation
                state.packageDetails = initialState.packageDetails;
                state.price = null;
                state.calculatedDistance = null;
                state.lastPriceCalculationParams = null;
                state.error = null;
            })
            .addCase(createPackageOrder.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
            })
            // Fetch User Orders
            .addCase(fetchUserPackageOrders.pending, (state) => {
                state.userOrdersStatus = "loading";
            })
            .addCase(fetchUserPackageOrders.fulfilled, (state, action) => {
                state.userOrdersStatus = "succeeded";
                state.userOrders = action.payload;
                state.userOrdersError = null;
            })
            .addCase(fetchUserPackageOrders.rejected, (state, action) => {
                state.userOrdersStatus = "failed";
                state.userOrdersError = action.payload;
            });
    },
});

export const {
    updatePackageDetails,
    setCalculatedDistance,
    resetPackageState,
    setLastPriceCalculationParams,
    updateOrderStatus,
    addNewOrder,
    clearPackageError,
} = packageSlice.actions;

export default packageSlice.reducer;
